import $ from 'jquery'
import Backbone from 'backbone'
import _ from 'lodash'
import icon from 'scoreboard/components/icon'
import Tooltip from 'scoreboard/components/tooltip'
import KeyCodes from 'scoreboard/components/key-codes'
import DateTimeControl from 'scoreboard/components/date-time-control'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import { to24hrTime, timeToSplitFieldsObject, dateTimeToSplitFieldsObject } from 'scoreboard/components/date-time-util'
import { getDateTimeString } from 'scoreboard/components/date-time-util'
import momentHelper from 'scoreboard/components/moment-helper'

/**
 * An input which displays a DateTimeControl (time or date/time picker) in a tooltip
 * and displays the selected value in that input.
 */
const TimeInput = Backbone.View.extend({

	className: 'time-input',

	/**
	 * @param {String} [options.name] The name attribute for the input element
	 * rendered here.
	 * @param {JQuery} [options.el] the input element to attach to. Is mutually
	 * exclusive to options.name
	 * @param {Object} [options.dateTime] The starting date/time for this input.
	 * @param {Boolean} [options.hasCalendarControl] True if the tooltip should include a
	 * calendar control alongside the time control, else false.
	 * @param {Boolean} [options.required] True if this input is required, else false.
	 * @param {TimeDisplayType} [options.timeDisplayType] how should the time portion be
	 * displayed?
	 */
	initialize(options = {}) {
		this.options = options
		this.dateTime = options.dateTime
		this.initialDisplay = getDateTimeString(
			options.dateTime, options.timeDisplayType, options.hasCalendarControl
		)
	},

	render() {
		this._setupInput()
		if (this.initialDisplay) this.input.val(this.initialDisplay)
		let CustomMarkedField = MarkedField.extend({
			val: this.val.bind(this),
			_val: this.getDateTime.bind(this)
		})

		let validators = this.options.required ? [requiredValidator] : []
		if (this.options.validators) {
			validators = _.concat(validators, this.options.validators)
		}
		this.markedField = new CustomMarkedField({
			el: this.input,
			validators,
			onBlur: () => {}
		}).render()
		let fieldIcon
		if (this.options.isDateTime) {
			fieldIcon = 'date-time'
		} else {
			fieldIcon = momentHelper.is24hrTimeType(this.options.timeDisplayType) ?
				'military-clock' : 'clock'
		}
		let svg = icon(fieldIcon, 'type-icon')
		this.input.after(svg)
		return this
	},

	_setupInput() {
		if (this.options.el) {
			this.input = this.options.el
			const initialVal = this.input.val()
			if (initialVal) {
				this.setDateTime(initialVal)
			}
		} else {
			this.input = $('<input/>', {
				name: this.options.name,
				type: 'text'
			}).appendTo(this.$el)
		}
		this.input
			.attr('readonly', true)
			.click(() => this._onClickInput())
			.keydown(this._onKeydownInput.bind(this))
	},

	val(dt) {
		if (arguments.length === 0) {
			if (this.options.hasCalendarControl) {
				return _.invoke(this.dateTime, 'moment.format', momentHelper.serverLocalDateTimeFormat)
			} else {
				return to24hrTime(_.result(this.dateTime, 'time'))
			}
		} else this.setDateTime(dt)
	},

	getDateTime() {
		return this.dateTime
	},

	/**
	 * Fills the controls input with the currently selected date/time
	 */
	_setDateTime(dt) {
		if (_.isNil(dt)) {
			return
		}

		this.dateTime = dt
		this.input.val(dt.string)
		this.markedField.validate()
		this.trigger('onSetDateTime', dt)
		this.input.trigger('input', this.val())
	},

	setDateTime(dt) {
		const hr24 = momentHelper.is24hrTimeType(this.options.timeDisplayType)
		if (typeof dt === 'string') {
			if (this.options.hasCalendarControl) {
				this.setDateTime(dateTimeToSplitFieldsObject(dt, undefined, hr24))
			} else {
				this.setDateTime({
					time: timeToSplitFieldsObject(dt, hr24)
				})
			}
			return
		}

		this.dateTime = dt
		this.input.val(getDateTimeString(dt, this.options.timeDisplayType))

		if (this.tooltip && !this.tooltip.removed) {
			this.dateTimeControl.updateDateTime(dt)
		}
	},

	_onClickInput() {
		// If there's nothing in the input and we click on it, reset the dateTime
		// so the dateTimeControl doesn't appear already filled out
		if (this.$('input').val() === '') {
			delete this.dateTime
		}

		this.dateTimeControl = new DateTimeControl({
			hasCalendarControl: this.options.hasCalendarControl,
			dateTime: this.dateTime,
			timeDisplayType: this.options.timeDisplayType
		})

		this.dateTimeControl.on('onSetDateTime', dt => this._setDateTime(dt))

		this.tooltip = new Tooltip({
			attachTo: this.input,
			content: this.dateTimeControl,
			arrow: 'center',
			extraClass: 'time-input-tooltip' + (this.options.hasCalendarControl ? ' has-calendar' : '')
		}).render()

		// If there's no calendar control, place focus on the hour input so no
		// mousing is necessary to quickly enter time
		if (!this.options.hasCalendarControl) {
			this.dateTimeControl.timeControl.focus()
		}
	},

	/**
	 * If the backspace key is pressed while the input is highlighted, clear it
	 */
	_onKeydownInput(e) {
		if (e.keyCode == KeyCodes.BACKSPACE) {
			this.clear()
		}
	},

	clear() {
		this.input.val('')
		_.invoke(this.dateTimeControl, 'clear')
		_.invoke(this.tooltip, 'remove')
	},

	remove() {
		_.invokeMap([this.markedField], 'remove')
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.timeControl, this.calendarControl, this.tooltip], 'remove')
	}
})

export default TimeInput

TimeInput.__filename__ = 'components/time-input.js'
